/**
 * Exponential Backoff Utility
 *
 * Implements exponential backoff cooldown mechanism similar to Gmail's retry system.
 * The cooldown duration doubles with each attempt: baseSeconds * 2^(attemptNumber - 1)
 *
 * Example progression with baseSeconds = 5:
 * - Attempt 1: 5s cooldown
 * - Attempt 2: 10s cooldown
 * - Attempt 3: 20s cooldown
 * - Attempt 4: 40s cooldown
 * - Attempt 5: 80s cooldown
 *
 * Usage:
 * ```typescript
 * import { calculateExponentialBackoff, calculateRemainingCooldown } from 'utils/exponentialBackoff';
 *
 * // Calculate cooldown duration for attempt number 3 with base 5 seconds
 * const cooldownSeconds = calculateExponentialBackoff(3, 5); // Returns 20
 *
 * // Calculate remaining cooldown time
 * const lastRetryTimestamp = Date.now() - 5000; // 5 seconds ago
 * const remaining = calculateRemainingCooldown(3, 5, lastRetryTimestamp); // Returns 15 (20 - 5)
 * ```
 */

/**
 * Calculate exponential backoff cooldown duration in seconds
 *
 * Formula: baseSeconds * 2^(attemptNumber - 1)
 *
 * @param attemptNumber - The attempt number (1-based, where 1 is the first retry)
 * @param baseSeconds - Base cooldown duration in seconds (default: 5)
 * @returns Cooldown duration in seconds
 *
 * @example
 * calculateExponentialBackoff(1, 5) // Returns 5
 * calculateExponentialBackoff(2, 5) // Returns 10
 * calculateExponentialBackoff(3, 5) // Returns 20
 * calculateExponentialBackoff(4, 5) // Returns 40
 */
export const calculateExponentialBackoff = (
  attemptNumber: number,
  baseSeconds: number = 5
): number => {
  if (attemptNumber <= 0) return 0;
  // Exponential backoff: baseSeconds * 2^(attemptNumber - 1)
  // Attempt 1: baseSeconds * 2^0 = baseSeconds
  // Attempt 2: baseSeconds * 2^1 = baseSeconds * 2
  // Attempt 3: baseSeconds * 2^2 = baseSeconds * 4
  return baseSeconds * Math.pow(2, attemptNumber - 1);
};

/**
 * Calculate remaining cooldown time in seconds based on last retry timestamp
 *
 * @param attemptNumber - The attempt number (1-based)
 * @param baseSeconds - Base cooldown duration in seconds (default: 5)
 * @param lastRetryTimestamp - Timestamp (in milliseconds) of the last retry attempt
 * @returns Remaining cooldown time in seconds (0 if cooldown has expired)
 *
 * @example
 * const lastRetry = Date.now() - 5000; // 5 seconds ago
 * calculateRemainingCooldown(2, 5, lastRetry) // Returns 5 (10s cooldown - 5s elapsed)
 * calculateRemainingCooldown(2, 5, Date.now() - 15000) // Returns 0 (cooldown expired)
 */
export const calculateRemainingCooldown = (
  attemptNumber: number,
  baseSeconds: number,
  lastRetryTimestamp: number
): number => {
  if (attemptNumber <= 0 || lastRetryTimestamp <= 0) return 0;

  const cooldownDuration = calculateExponentialBackoff(attemptNumber, baseSeconds);
  const elapsed = Math.floor((Date.now() - lastRetryTimestamp) / 1000);
  return Math.max(0, cooldownDuration - elapsed);
};

/**
 * Check if a retry attempt is currently in cooldown
 *
 * @param attemptNumber - The attempt number (1-based)
 * @param baseSeconds - Base cooldown duration in seconds (default: 5)
 * @param lastRetryTimestamp - Timestamp (in milliseconds) of the last retry attempt
 * @returns true if still in cooldown, false otherwise
 */
export const isInCooldown = (
  attemptNumber: number,
  baseSeconds: number,
  lastRetryTimestamp: number
): boolean => {
  return calculateRemainingCooldown(attemptNumber, baseSeconds, lastRetryTimestamp) > 0;
};

/**
 * Format cooldown seconds for display (e.g. "5" or "10s")
 *
 * @param seconds - Remaining cooldown in seconds
 * @returns Formatted string
 */
export const formatCooldownSeconds = (seconds: number): string =>
  seconds + (seconds === 1 ? "" : "s");
